// oxlint-disable max-classes-per-file
import { StatusCodes } from 'http-status-codes';
import { match, P } from 'ts-pattern';
import { z } from 'zod';
import { typeGuardFromSchema } from './schemas';

/**
 * Error class for handling store not found errors.
 * It extends the built-in Error class and includes a slug property
 * to identify the store that was not found.
 * The error message is constructed based on the provided slug if a message is not provided.
 * @param {Object} options - The options for the error.
 * @param {string} options.slug - The slug of the store that was not found.
 * @param {string} [options.message] - An optional custom error message.
 * @param {ErrorOptions} [options] - Additional error options.
 * @extends {Error}
 */
export class StoreNotFoundError extends Error {
  slug: string;

  constructor({
    slug,
    message,
    ...options
  }: ErrorOptions & { message?: string; slug: string }) {
    super(message ?? `Store with slug "${slug}" not found.`, options);
    this.name = 'StoreNotFoundError';
    this.slug = slug;
  }
}

/**
 * Error class for handling API response errors.
 * It extends the built-in Error class and includes additional properties
 * such as the response object and the body of the response.
 * The error message is constructed based on the response status and body.
 *  @template T - The type of the response body.
 *  @param {Object} options - The options for the error.
 *  @param {Response} options.response - The response object from the API.
 *  @param {string} [options.message] - An optional custom error message.
 *  @param {T} [options.body] - The body of the response, if available.
 *  @param {ErrorOptions} [options] - Additional error options.
 *  @extends {Error}
 *  @example
 *  const error = new ResponseError({
 *    response: new Response('Not Found', { status: 404 }),
 *    body: { message: 'Resource not found' },
 *    message: 'Custom error message',
 *  });
 *  console.error(error.message); // Outputs: "Custom error message"
 *  console.error(error.response.status); // Outputs: 404
 *  console.error(error.body); // Outputs: { message: 'Resource not found' }
 */
export class ResponseError<T = unknown> extends Error {
  body?: T;

  response: Response;

  constructor({
    message,
    response,
    body,
    ...options
  }: { response: Response; message?: string; body?: T } & ErrorOptions) {
    super(ResponseError.#getErrorMessage({ response, body, message }), options);
    this.name = 'ResponseError';
    this.body = body;
    this.response = response;
  }

  static #getErrorMessage(
    input: Pick<
      ConstructorParameters<typeof ResponseError>[0],
      'response' | 'body' | 'message'
    >,
  ): string {
    const isApiError = typeGuardFromSchema(
      z.object({
        message: z.string(),
      }),
    );

    return match(input)
      .with({ message: P.string }, ({ message }) => message)
      .with({ body: P.when(isApiError) }, ({ body: { message } }) => message)
      .with(
        { response: { status: StatusCodes.FORBIDDEN } },
        () => 'Sorry, not authorized',
      )
      .with(
        { response: { status: StatusCodes.NOT_FOUND } },
        () => 'Item not found',
      )
      .otherwise(
        () =>
          'Something went wrong, please try again. If problem persists, please contact us.',
      );
  }
}

/**
 * Error class for handling network-related errors.
 * Primarily used to help identify retry conditions with p-retry due to Sentry/Safari issues for network errors.
 * @param {string} [message] - The error message.
 * @param {ErrorOptions} [options] - Additional error options.
 * @extends {Error}
 */
export class NetworkError extends Error {
  constructor(message?: string, options?: ErrorOptions) {
    super(message, options);
    this.name = 'NetworkError';
  }
}
